import type { ToOptions } from "@tanstack/react-router";

export type ProjectModule = "audit" | "corporate-finance" | "fas";

/** A project as its pages see it: the active project and the root above it. */
export interface ProjectRef {
  module: ProjectModule;
  projectId: string;
  rootProjectId: string;
}

type RouteTo = NonNullable<ToOptions["to"]>;

// a page whose full path the route tree does not know becomes `never`, so a
// wrong suffix fails to compile
export const projectModule = <
  const Base extends RouteTo,
  const Pages extends Record<string, string>,
>(
  base: Base,
  pages: Pages & {
    [Page in keyof Pages]: `${Base}${Pages[Page]}` extends RouteTo
      ? Pages[Page]
      : never;
  },
) => ({ base, pages });

// a page a module lacks is one it does not offer, never a fallback to another
// module's route
const PROJECT_MODULES = {
  audit: projectModule("/auditor/clients/$id", {
    "": "",
    "input-process": "/input-process",
    "audit-files": "/input-process",
    "trial-balance": "/input-process/trial-balance",
    documents: "/input-process/documents",
    classification: "/input-process/classification",
    "statement-extraction": "/input-process/statement-extraction",
    reconciliation: "/input-process/reconciliation",
    validations: "/validations",
    workflows: "/tasks",
    "document-analysis": "/document-analysis",
    "analysis-results": "/analysis/$rid/results",
    "financial-explorer": "/financial-explorer",
    "agbw-overview": "/agbw-overview",
    "continuous-auditor-results": "/continuous-auditor-results",
  }),
  "corporate-finance": projectModule("/corporate-finance/projects/$id", {
    "": "",
    "input-process": "/input-process",
    "audit-files": "/input-process",
    documents: "/input-process/documents",
    classification: "/account-mapping",
    "statement-extraction": "/input-process/statement-extraction",
    reconciliation: "/input-process/reconciliation",
    workflows: "/workflows",
    "document-analysis": "/document-analysis",
    "analysis-results": "/analysis/$rid/results",
    "financial-explorer": "/financial-explorer",
  }),
  fas: projectModule("/fas/projects/$id", {
    "": "",
    "input-process": "/input-process",
    classification: "/account-mapping",
    workflows: "/workflows",
    "financial-explorer": "/financial-explorer",
  }),
} satisfies Record<ProjectModule, unknown>;

export type ProjectPage = {
  [Module in ProjectModule]: keyof (typeof PROJECT_MODULES)[Module]["pages"];
}[ProjectModule];

export type ProjectBaseRoute = (typeof PROJECT_MODULES)[ProjectModule]["base"];

export const projectBaseRoute = (module: ProjectModule): ProjectBaseRoute =>
  PROJECT_MODULES[module].base;

/** Whether a module serves document analysis at all, read off its pages. */
export const hasDocumentAnalysisRoutes = (module: ProjectModule): boolean =>
  "document-analysis" in PROJECT_MODULES[module].pages;

export function getHoldingEntitySearch(
  projectId: string,
  activeEntityProjectId?: string,
) {
  if (!activeEntityProjectId || activeEntityProjectId === projectId) {
    return undefined;
  }

  return {
    entityProjectId: activeEntityProjectId,
  };
}

// every module addresses the root in `$id` and a different active project as
// `entityProjectId`; audit's client route canonicalizes any dossier url to
// exactly this shape
export function projectPageHref(
  project: ProjectRef,
  page: ProjectPage,
  extra: {
    params?: Record<string, string>;
    search?: Record<string, unknown>;
  } = {},
): ToOptions | undefined {
  const { base, pages } = PROJECT_MODULES[project.module];
  const suffix = (pages as Partial<Record<ProjectPage, string>>)[page];
  if (suffix === undefined) return undefined;

  const entitySearch = getHoldingEntitySearch(
    project.rootProjectId,
    project.projectId,
  );
  return {
    // checked per page by projectModule
    to: `${base}${suffix}` as RouteTo,
    params: { ...extra.params, id: project.rootProjectId },
    search:
      extra.search || entitySearch
        ? { ...extra.search, ...entitySearch }
        : undefined,
  };
}
